import React, { useCallback, useEffect, useMemo, useState } from "react"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import List from "../../../components/List/List"
import { companyMemory, orderMemory } from "../../../Objects/Memory/Memory"
import { OrderEntity } from "../../../Objects/Order/OrderEntity"
import { ORDER_LIST_PAGE_SIZE, type OrderPaginationState, useGetOrder } from "../../../hooks/Order/useGetOrder"
import { useReloadMemory } from "../../../hooks/useReloadMemory"
import { useTranslation } from "react-i18next"
import { OrderPupop } from "./OrderPupop"
import { OrderPupopClose } from "./OrderPupopClose"
import { getStoreNow } from "../../../utils/timeZone"
import { normalizeReservationDateForInput } from "../../../Extensions/ReservationExtensions"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { API_ROUTES } from "../../../configuration/RoutesApi"
import { ERequestType } from "../../../Enum/ERequestType"

export const ReservedOrdersPage: React.FC = () => {
  const { t } = useTranslation("order")

  const { handleGetOrder } = useGetOrder()

  const { handleReloadToMemory } = useReloadMemory()

  const [dateStart] = useState<string>(() => {
    const date = getStoreNow(companyMemory.timeZoneId)
    date.setFullYear(date.getFullYear() - 5)
    return date.toInputDate()
  })

  const [dateEnd] = useState<string>(() => {
    const date = getStoreNow(companyMemory.timeZoneId)
    date.setFullYear(date.getFullYear() + 5)
    return date.toInputDate()
  })

  const [orderData, setOrderData] = useState<OrderEntity | undefined>(undefined)

  const [enableCommands, setEnableCommands] = useState(false)

  const [paginationState, setPaginationState] = useState<OrderPaginationState>({
    pageNumber: 1,
    pageSize: ORDER_LIST_PAGE_SIZE,
    totalCount: 0,
    hasNextPage: false,
  })

  const [isLoadingMore, setIsLoadingMore] = useState(false)

  const reservedOrders = useMemo(() => {
    return orderMemory.listData
      .map((order) => {
        const items = [...order.orderProducts, ...order.orderCatalogItems]
        const reservations = items
          .map((item) => ({
            itemName: item.getName(),
            reservationDate: normalizeReservationDateForInput(item.reservationDate),
            reservationTime: item.reservationTime,
            isReservation: item.isReservation,
          }))
          .filter((item) => item.isReservation)

        return {
          order,
          reservations,
        }
      })
      .filter((entry) => entry.reservations.length > 0)
  }, [orderMemory.listData])

  const loadReservedOrdersPage = useCallback(async (pageNumber: number, append: boolean) => {
    const nextPaginationState = await handleGetOrder(
      () => {},
      dateStart,
      dateEnd,
      API_ROUTES.ORDER.GET_ALL_RESERVED,
      ERequestType.Store,
      false,
      false,
      {
        pageNumber,
        pageSize: ORDER_LIST_PAGE_SIZE,
        append,
      },
    )

    if (nextPaginationState) {
      setPaginationState(nextPaginationState)
    }
  }, [dateEnd, dateStart, handleGetOrder])

  const refreshReservedOrders = async () => {
    await handleReloadToMemory()

    await loadReservedOrdersPage(1, false)
  }

  const handleLoadMoreReservedOrders = useCallback(async () => {
    if (isLoadingMore || !paginationState.hasNextPage) {
      return
    }

    setIsLoadingMore(true)

    try {
      await loadReservedOrdersPage(paginationState.pageNumber + 1, true)
    } finally {
      setIsLoadingMore(false)
    }
  }, [isLoadingMore, loadReservedOrdersPage, paginationState.hasNextPage, paginationState.pageNumber])

  useEffect(() => {
    void refreshReservedOrders()
  }, [])

  const handleSwitchEditeOrAdd = (status: boolean) => {
    setOrderData(undefined)
    setEnableCommands(status)
  }

  const handleOpenOrder = (id: number) => {
    const order = reservedOrders.find((entry) => entry.order.id === id)?.order

    if (!order) {
      return
    }

    handleSwitchEditeOrAdd(true)
    setOrderData(order)
  }

  const reservedCards = reservedOrders.map((entry, index) => {
    const card = orderMemory.getCard(entry.order, index)
    return {
      ...card,
      dataFilter: [
        ...(card.dataFilter ?? []),
        ...entry.reservations.map((reservation) => reservation.itemName),
        ...entry.reservations.map((reservation) => reservation.reservationDate),
      ],
    }
  })

  const groupedByDate = useMemo(() => {
    const map = new Map<string, number>()

    reservedOrders.forEach((entry) => {
      entry.reservations.forEach((reservation) => {
        const key = reservation.reservationDate
        if (!key) {
          return
        }
        map.set(key, (map.get(key) ?? 0) + 1)
      })
    })

    return [...map.entries()].sort((a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime())
  }, [reservedOrders])

  const weeklyCount = groupedByDate
    .filter(([date]) => {
      const value = new Date(`${date}T00:00:00`)
      const diffDays = Math.ceil((value.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
      return diffDays >= 0 && diffDays <= 7
    })
    .reduce((sum, [, count]) => sum + count, 0)

  const monthlyCount = groupedByDate
    .filter(([date]) => {
      const value = new Date(`${date}T00:00:00`)
      const diffDays = Math.ceil((value.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
      return diffDays >= 0 && diffDays <= 31
    })
    .reduce((sum, [, count]) => sum + count, 0)

  return (
    <div className="h-[calc(100vh-150px)] w-full overflow-auto p-4 md:p-0">
      <div className="space-y-3">
        <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
          <div className="relative overflow-hidden rounded-xl border-2 border-orange-200 bg-gradient-to-r from-white via-orange-50 to-orange-100 p-5 shadow-sm">
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-orange-300 via-orange-500 to-amber-300" />

            <div className="mb-3 inline-flex items-center gap-2 rounded-lg bg-orange-100 px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] text-orange-600">
              📅 {t("reserved_orders.badge")}
            </div>

            <h2 className="text-2xl font-black tracking-tight text-slate-800 md:text-3xl">
              {t("reserved_orders.title")}
            </h2>

            <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600">{t("reserved_orders.subtitle")}</p>

            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="rounded-xl border-2 border-orange-200 bg-white px-4 py-4 shadow-sm">
                <div className="text-xs font-bold uppercase tracking-wide text-slate-500">
                  {t("reserved_orders.cards.reserved_commands")}
                </div>

                <div className="mt-2 text-2xl font-black text-slate-800">{reservedOrders.length}</div>
              </div>

              <div className="rounded-xl border-2 border-orange-200 bg-white px-4 py-4 shadow-sm">
                <div className="text-xs font-bold uppercase tracking-wide text-slate-500">
                  {t("reserved_orders.cards.weekly_schedule")}
                </div>

                <div className="mt-2 text-2xl font-black text-slate-800">{weeklyCount}</div>
              </div>

              <div className="rounded-xl border-2 border-orange-200 bg-white px-4 py-4 shadow-sm">
                <div className="text-xs font-bold uppercase tracking-wide text-slate-500">
                  {t("reserved_orders.cards.monthly_schedule")}
                </div>

                <div className="mt-2 text-2xl font-black text-slate-800">{monthlyCount}</div>
              </div>
            </div>
          </div>
        </Animate>

        <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
          <div className="rounded-xl border-2 border-orange-200 bg-white p-4 shadow-sm">
            <h3 className="text-base font-black text-slate-800">{t("reserved_orders.date_breakdown")}</h3>
            <div className="mt-2 flex flex-wrap gap-2">
              {groupedByDate.map(([date, count]) => (
                <span
                  key={date}
                  className="rounded-full border border-orange-200 bg-orange-50 px-3 py-1 text-xs font-semibold text-orange-700"
                >
                  {`${date}T00:00:00`.toReadableDate("pt-BR", true)}: {t("reserved_orders.items_count", { count })}
                </span>
              ))}
              {groupedByDate.length === 0 && (
                <span className="text-sm text-slate-500">{t("reserved_orders.no_reservations")}</span>
              )}
            </div>
          </div>
        </Animate>

        <List
          data={reservedCards}
          messageCount={t("list:label_order_page_message_count")}
          messageEmpty={t("reserved_orders.empty")}
          nameFindSearch={t("list:label_order_page_place_holder")}
          handleRefreshButton={refreshReservedOrders}
          showFilterStatus={false}
          handleOnClick={(id) => handleOpenOrder(id)}
          hasMore={paginationState.hasNextPage}
          isLoadingMore={isLoadingMore}
          onLoadMore={handleLoadMoreReservedOrders}
        />

        {enableCommands && ((orderData && !orderData.orderPaymentEntity.isClosed()) || !orderData) && (
          <OrderPupop
            order={orderData}
            title={orderData ? t("title_popup_edite") : t("title_popup_create")}
            onClonse={() => handleSwitchEditeOrAdd(false)}
            onSave={refreshReservedOrders}
          />
        )}

        {enableCommands && orderData && orderData.orderPaymentEntity.isClosed() && (
          <OrderPupopClose
            order={orderData}
            onClonse={() => handleSwitchEditeOrAdd(false)}
            onSave={refreshReservedOrders}
          />
        )}
      </div>
    </div>
  )
}
